// Generalisiertes Website-Modul (Anfragen, Besucherstatistik, Wochenbericht, Einbau-Code) für
// strandhussiedlung-ruegen.de und schwalbennest-restaurant.de — analog zur ComPanel.js/ComStats.js der .com
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Globe, RefreshCw, Loader2, Send, CheckCircle2, RotateCcw, Mail, Phone, ExternalLink, Copy, Trash2, BarChart3, Code2, ShieldCheck, Bed } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./components/ui/dialog";
import { SiteGoogle } from "./SiteGoogle";

const API = (slug) => `${process.env.REACT_APP_BACKEND_URL}/api/sites/${slug}`;
const err = (e, f) => e.response?.data?.detail || f;
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short", timeZone: "Europe/Berlin" }) : "–");
const btn = "flex items-center gap-2 text-xs px-3 py-1.5 rounded-md border border-white/10 hover:bg-white/5 transition-colors disabled:opacity-50";
const Card = ({ children, testid }) => <div className="bg-eb-surface border border-white/5 rounded-xl p-5" data-testid={testid}>{children}</div>;

// Ob die angemeldete Person Admin ist (für Löschen/Wochenbericht-senden-Buttons, Backend blockt ohnehin mit 403)
function useIsAdmin() {
  const [admin, setAdmin] = useState(false);
  useEffect(() => { axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`).then((r) => setAdmin(r.data.role === "admin")).catch(() => {}); }, []);
  return admin;
}

// Zahl offener Anfragen fürs Cockpit-Menü
export function useSiteOpenCount(slug, enabled) {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!enabled) return undefined;
    const load = () => axios.get(`${API(slug)}/inquiries/count`).then((r) => setN(r.data.offen)).catch(() => {});
    load();
    const id = setInterval(load, 60000);
    const ev = `site-${slug}-changed`;
    window.addEventListener(ev, load);
    return () => { clearInterval(id); window.removeEventListener(ev, load); };
  }, [slug, enabled]);
  return n;
}

function Stat({ label, value, testid }) {
  return (
    <div className="bg-black/25 rounded-lg p-3" data-testid={testid}>
      <div className="text-sm font-semibold">{value}</div>
      <div className="text-[11px] text-eb-muted uppercase tracking-wider mt-0.5">{label}</div>
    </div>
  );
}

function CodeBox({ title, hint, code, testid }) {
  const [done, setDone] = useState(false);
  const copy = () => { navigator.clipboard?.writeText(code); setDone(true); setTimeout(() => setDone(false), 2000); };
  return (
    <div className="bg-black/25 rounded-lg p-3 space-y-2" data-testid={testid}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <b className="text-xs">{title}</b>
        <button className={btn} onClick={copy} data-testid={`${testid}-copy`}><Copy size={12} /> {done ? "Kopiert" : "Kopieren"}</button>
      </div>
      <p className="text-[11px] text-eb-muted">{hint}</p>
      <pre className="text-[10px] max-h-28 overflow-auto bg-black/40 rounded p-2 whitespace-pre-wrap break-all">{code}</pre>
    </div>
  );
}

function EmbedCard({ slug, meta }) {
  const [d, setD] = useState(null);
  const [msg, setMsg] = useState("");
  useEffect(() => { axios.get(`${API(slug)}/embed`).then((r) => setD(r.data)).catch((e) => setMsg(err(e, "Einbau-Code nicht ladbar"))); }, [slug]);
  return (
    <Card testid={`site-${slug}-embed`}>
      <h3 className="font-heading font-semibold flex items-center gap-2"><Code2 size={16} /> Einbindung auf {meta?.host}</h3>
      <p className="text-xs text-eb-muted mt-1">
        {d?.listener_js
          ? "Zwei Bausteine für ein Divi-Code-Modul: A läuft seitenweit und zählt Besucher. B hört nur auf das bestehende Kontaktformular mit (ohne es zu verändern) und spiegelt neue Anfragen zusätzlich ins Cockpit."
          : "Baustein für ein Divi-Code-Modul (z. B. Header-Vorlage): zählt Besucher cookielos, ohne IP-Speicherung."}
      </p>
      {msg && <p className="text-sm text-rose-400 mt-3">{msg}</p>}
      {!d ? <Loader2 className="animate-spin mt-3" size={18} /> : (
        <div className={`grid gap-3 mt-4 ${d.listener_js || d.booking_js ? "lg:grid-cols-2" : ""}`}>
          <CodeBox testid={`site-${slug}-embed-tracking`} title="A · Besucherzählung (seitenweit)" code={d.tracking_js}
            hint="In Divi Theme Builder → Header-Vorlage als Code-Modul einfügen." />
          {d.listener_js && (
            <CodeBox testid={`site-${slug}-embed-form`} title={`B · Formular „${d.form_selector}“ mitlesen`} code={d.listener_js}
              hint="Irgendwo auf der Formular-Seite als Code-Modul einfügen (z. B. unter dem Formular). Das bestehende Formular bleibt unverändert und sendet wie gewohnt ab." />
          )}
          {d.booking_js && (
            <CodeBox testid={`site-${slug}-embed-booking`} title="C · easybooking-Buchungsfenster" code={d.booking_js}
              hint="Als Code-Modul auf der gewünschten Seite einfügen (z. B. „Buchen“ oder Startseite). Lädt jQuery und die Buchungsmaske automatisch nach." />
          )}
        </div>
      )}
    </Card>
  );
}

function LiveStatusCard({ slug }) {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const load = useCallback(() => axios.get(`${API(slug)}/siteguard`).then((r) => setD(r.data)).catch((e) => setMsg(err(e, "Status nicht ladbar"))), [slug]);
  useEffect(() => { load(); }, [load]);
  const scan = async () => {
    setBusy(true); setMsg("");
    try { await axios.post(`${API(slug)}/siteguard/scan`); await load(); }
    catch (e) { setMsg(err(e, "Prüfung fehlgeschlagen")); }
    setBusy(false);
  };
  if (!d) return msg ? <p className="text-sm text-rose-400">{msg}</p> : null;
  const score = d.health_score;
  const color = score == null ? "text-eb-muted" : score >= 80 ? "text-emerald-300" : score >= 50 ? "text-amber-300" : "text-rose-300";
  return (
    <Card testid={`site-${slug}-livestatus`}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h3 className="font-heading font-semibold flex items-center gap-2"><ShieldCheck size={16} /> Live-Status</h3>
        <button className={btn} disabled={busy} onClick={scan} data-testid={`site-${slug}-livestatus-scan`}>
          {busy ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} Jetzt prüfen</button>
      </div>
      {!d.connected ? <p className="text-sm text-eb-muted mt-3">Noch nicht überwacht.</p> : (
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mt-3">
          <div className="bg-black/25 rounded-lg p-3" data-testid={`site-${slug}-livestatus-score`}>
            <div className={`text-sm font-semibold ${color}`}>{score != null ? `${score} / 100` : "–"}</div>
            <div className="text-[11px] text-eb-muted uppercase tracking-wider mt-0.5">Gesundheits-Score</div>
          </div>
          <Stat label="Offene Befunde" value={d.open_issues} testid={`site-${slug}-livestatus-issues`} />
          <Stat label="Letzte Prüfung" value={fmt(d.last_scan)} testid={`site-${slug}-livestatus-lastscan`} />
        </div>
      )}
      {msg && <p className="text-xs text-rose-400 mt-2">{msg}</p>}
    </Card>
  );
}

export function EasybookingCard({ slug, api }) {
  const base = api || API(slug);
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  useEffect(() => { axios.get(`${base}/easybooking`).then((r) => setD(r.data)).catch((e) => setMsg(err(e, "Belegung nicht ladbar"))); }, [base]);
  const refresh = async () => {
    setBusy(true); setMsg("");
    try { setD((await axios.post(`${base}/easybooking/refresh`)).data); }
    catch (e) { setMsg(err(e, "Aktualisieren fehlgeschlagen")); }
    setBusy(false);
  };
  if (!d) return msg ? <p className="text-sm text-rose-400">{msg}</p> : null;
  if (!d.connected) return null;
  const t = d.totals;
  const freiAb = (c) => {
    if (c.free > 0) return <span className="text-emerald-300 font-semibold">heute frei</span>;
    if (!d.deep) return <span className="text-eb-muted">„Aktualisieren“ tippen</span>;
    if (c.frei_ab_unknown) return <span className="text-eb-muted">unbekannt</span>;
    if (!c.frei_ab) return <span className="text-eb-muted">über 120 Tage belegt</span>;
    return <span className="text-amber-300">ab {fmt(c.frei_ab).split(",")[0]}</span>;
  };
  return (
    <Card testid={`site-${slug}-easybooking`}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h3 className="font-heading font-semibold flex items-center gap-2"><Bed size={16} /> easybooking-Belegung ({d.categories.length} Kategorien, {d.totals.units} Einheiten)</h3>
        <div className="flex items-center gap-3">
          <span className="text-[11px] text-eb-muted">verbunden seit {fmt(d.validated_at)}{d.cached_at ? ` · Belegung Stand ${fmt(d.cached_at)}` : ""}</span>
          <button className={btn} disabled={busy} onClick={refresh} data-testid={`site-${slug}-eb-refresh`}>
            {busy ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} Aktualisieren</button>
        </div>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-3">
        <Stat label="Einheiten gesamt" value={t.units} testid={`site-${slug}-eb-units`} />
        <Stat label="Belegt heute" value={t.occupied} testid={`site-${slug}-eb-occupied`} />
        <Stat label="Frei heute" value={t.free} testid={`site-${slug}-eb-free`} />
        <Stat label="Auslastung" value={`${t.occupancy_pct}%`} testid={`site-${slug}-eb-rate`} />
      </div>
      <div className="mt-4 overflow-x-auto" data-testid={`site-${slug}-eb-categories`}>
        <table className="w-full text-xs border-collapse">
          <thead>
            <tr className="text-left text-eb-muted uppercase tracking-wider text-[10px]">
              <th className="py-1.5 pr-3">Bungalow</th>
              <th className="py-1.5 pr-3">Personen</th>
              <th className="py-1.5 pr-3">Preis/Nacht</th>
              <th className="py-1.5 pr-3">Heute</th>
              <th className="py-1.5">Wieder frei</th>
            </tr>
          </thead>
          <tbody>
            {d.categories.map((c) => (
              <tr key={c.id} className="border-t border-white/5" data-testid={`site-${slug}-eb-row-${c.id}`}>
                <td className="py-1.5 pr-3 font-medium">{c.name}</td>
                <td className="py-1.5 pr-3 text-eb-muted">{c.persons || "–"}</td>
                <td className="py-1.5 pr-3 text-eb-muted">{c.price ? `${c.price} €` : "–"}</td>
                <td className="py-1.5 pr-3">
                  {c.free == null ? <span className="text-eb-muted">unbekannt</span> : c.free > 0
                    ? <span className="text-emerald-300 font-semibold">frei</span> : <span className="text-eb-muted">belegt</span>}
                </td>
                <td className="py-1.5">{freiAb(c)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {t.unknown > 0 && <p className="text-[11px] text-eb-muted mt-2">{t.unknown} Kategorie(n) gerade nicht abrufbar – nicht in der Auslastung enthalten.</p>}
      {msg && <p className="text-xs text-rose-400 mt-2">{msg}</p>}
    </Card>
  );
}

function ReplyDialog({ slug, inq, onClose, onDone }) {
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  useEffect(() => {
    if (!inq) return;
    setDraft(null); setMsg("");
    axios.get(`${API(slug)}/inquiries/${inq.id}/template`).then((r) => setDraft(r.data)).catch((e) => setMsg(err(e, "Vorlage nicht ladbar")));
  }, [slug, inq]);
  const send = async () => {
    setBusy(true); setMsg("");
    try { onDone((await axios.post(`${API(slug)}/inquiries/${inq.id}/reply`, draft)).data); }
    catch (e) { setMsg(err(e, "Senden fehlgeschlagen")); }
    setBusy(false);
  };
  return (
    <Dialog open={!!inq} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-eb-surface border-white/10 text-eb-fg max-w-2xl" data-testid={`site-${slug}-reply-dialog`}>
        <DialogHeader><DialogTitle>Schnellantwort an {inq?.name}</DialogTitle></DialogHeader>
        <p className="text-xs text-eb-muted">An {inq?.email}</p>
        {!draft ? <Loader2 className="animate-spin" size={18} /> : (
          <div className="space-y-3">
            <input value={draft.subject} onChange={(e) => setDraft({ ...draft, subject: e.target.value })}
              className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm" data-testid={`site-${slug}-reply-subject`} />
            <textarea value={draft.text} onChange={(e) => setDraft({ ...draft, text: e.target.value })} rows={12}
              className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm leading-relaxed" data-testid={`site-${slug}-reply-text`} />
            <p className="text-[11px] text-eb-muted">Texte in [eckigen Klammern] ersetzen.</p>
          </div>
        )}
        {msg && <p className="text-sm text-rose-400" data-testid={`site-${slug}-reply-error`}>{msg}</p>}
        <div className="flex justify-end gap-2">
          <button className={btn} onClick={onClose} data-testid={`site-${slug}-reply-cancel`}>Abbrechen</button>
          <button onClick={send} disabled={busy || !draft} data-testid={`site-${slug}-reply-send`}
            className="flex items-center gap-2 text-sm px-4 py-2 rounded-md bg-eb-primary text-black font-semibold hover:opacity-90 disabled:opacity-50">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Antwort senden
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const confirmText = (m) => (m.sent ? "gesendet" : m.skipped ? "übersprungen (Testadresse)" : m.queued ? "wird gesendet" : `fehlgeschlagen${m.error ? ` – ${m.error}` : ""}`);

function InquiryRow({ slug, r, open, toggle, onReply, onStatus, onDelete, isAdmin }) {
  const done = r.status === "beantwortet";
  return (
    <div className="border-t border-white/5" data-testid={`site-${slug}-inq-${r.id}`}>
      <button onClick={toggle} className="w-full text-left py-3 grid md:grid-cols-[110px_1fr_120px] gap-2 items-center hover:bg-white/[0.02] px-1" data-testid={`site-${slug}-inq-open-${r.id}`}>
        <span className="text-xs text-eb-muted">{fmt(r.created_at)}</span>
        <span className="text-sm min-w-0"><b>{r.name}</b> <span className="text-eb-muted">· {r.summary}</span></span>
        <span className={`text-[11px] font-semibold w-fit px-2 py-0.5 rounded-full ${done ? "bg-emerald-500/15 text-emerald-300" : "bg-amber-500/15 text-amber-300"}`} data-testid={`site-${slug}-inq-status-${r.id}`}>
          {done ? "beantwortet" : "offen"}</span>
      </button>
      {open && (
        <div className="pb-4 px-1" data-testid={`site-${slug}-inq-detail-${r.id}`}>
          <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1 text-sm bg-black/20 rounded-lg p-3">
            {r.fields.map((f) => <div key={f.key} className="min-w-0"><span className="text-eb-muted text-xs">{f.label}: </span><span className="break-words whitespace-pre-wrap">{f.value}</span></div>)}
          </div>
          {r.replies.length > 0 && <p className="text-[11px] text-emerald-300 mt-2">Beantwortet {fmt(r.answered_at)} von {r.answered_by} · „{r.replies[r.replies.length - 1].subject}“</p>}
          {done && !r.replies.length && <p className="text-[11px] text-emerald-300 mt-2">Als beantwortet markiert {fmt(r.answered_at)} von {r.answered_by}</p>}
          {r.confirm_mail && <p className="text-[11px] text-eb-muted mt-2">Eingangsbestätigung an den Gast: {confirmText(r.confirm_mail)}{!done && r.reminder_count > 0 ? ` · ${r.reminder_count}× erinnert` : ""}</p>}
          <div className="flex gap-2 mt-3 flex-wrap">
            <button onClick={() => onReply(r)} data-testid={`site-${slug}-inq-reply-${r.id}`}
              className="flex items-center gap-2 text-xs px-3 py-1.5 rounded-md bg-eb-primary text-black font-semibold hover:opacity-90"><Send size={13} /> Schnellantwort</button>
            <button className={btn} onClick={() => onStatus(r, done ? "offen" : "beantwortet")} data-testid={`site-${slug}-inq-toggle-${r.id}`}>
              {done ? <><RotateCcw size={13} /> Wieder öffnen</> : <><CheckCircle2 size={13} /> Als beantwortet markieren</>}</button>
            <a className={btn} href={`mailto:${r.email}`} data-testid={`site-${slug}-inq-mail-${r.id}`}><Mail size={13} /> {r.email}</a>
            {r.telefon && <a className={btn} href={`tel:${r.telefon.replace(/[^\d+]/g, "")}`} data-testid={`site-${slug}-inq-tel-${r.id}`}><Phone size={13} /> {r.telefon}</a>}
            {isAdmin && <button className={`${btn} ml-auto text-rose-300 border-rose-400/30`} onClick={() => onDelete(r)} data-testid={`site-${slug}-inq-delete-${r.id}`}><Trash2 size={13} /> Löschen</button>}
          </div>
        </div>
      )}
    </div>
  );
}

function WeeklyReport({ slug, isAdmin }) {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const load = useCallback(() => axios.get(`${API(slug)}/weekly-report`).then((r) => setD(r.data)).catch((e) => setMsg(err(e, "Wochenbericht nicht ladbar"))), [slug]);
  useEffect(() => {
    load();
    const ev = `site-${slug}-changed`;
    window.addEventListener(ev, load);
    return () => window.removeEventListener(ev, load);
  }, [slug, load]);
  const send = async () => {
    if (!window.confirm(`Wochenbericht jetzt an ${d.to.join(" und ")} senden?`)) return;
    setBusy(true); setMsg("");
    try {
      const r = await axios.post(`${API(slug)}/weekly-report/send`);
      setMsg(r.data.mail.every((m) => m.sent) ? "Wochenbericht verschickt." : `Versand fehlgeschlagen: ${r.data.mail.filter((m) => !m.sent).map((m) => m.to).join(", ")}`);
      load();
    } catch (e) { setMsg(err(e, "Versand fehlgeschlagen")); }
    setBusy(false);
  };
  if (!d) return msg ? <p className="text-sm text-rose-400 mt-3">{msg}</p> : null;
  const s = d.stats;
  return (
    <div className="mt-4 pt-4 border-t border-white/5" data-testid={`site-${slug}-weekly`}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-sm"><b>Wochenbericht</b> <span className="text-eb-muted text-xs">· {d.schedule} an {d.to.join(" + ")}</span></div>
        <div className="flex gap-2">
          <a className={btn} href={`${API(slug)}/weekly-report/preview`} target="_blank" rel="noreferrer" data-testid={`site-${slug}-weekly-preview`}><ExternalLink size={13} /> Vorschau</a>
          {isAdmin && <button className={btn} disabled={busy} onClick={send} data-testid={`site-${slug}-weekly-send`}>
            {busy ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} Jetzt senden</button>}
        </div>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-3">
        <Stat label={`Neu · ${s.label}`} value={s.new} testid={`site-${slug}-weekly-new`} />
        <Stat label="Beantwortet (7 Tage)" value={s.answered} testid={`site-${slug}-weekly-answered`} />
        <Stat label="Noch offen" value={s.open} testid={`site-${slug}-weekly-open`} />
        <Stat label="Ø Antwortzeit" value={s.avg || "–"} testid={`site-${slug}-weekly-avg`} />
      </div>
      {d.last && <p className="text-[11px] text-eb-muted mt-2">Zuletzt {d.last.source === "cron" ? "automatisch" : "von Hand"} {fmt(d.last.at)}
        {d.last.mail.length ? ` · ${d.last.mail.filter((m) => m.sent).length}/${d.last.mail.length} E-Mails zugestellt` : " · ohne E-Mail (Vorschau-Umgebung)"}</p>}
      {msg && <p className="text-xs text-eb-muted mt-2">{msg}</p>}
    </div>
  );
}

function Inquiries({ slug, title }) {
  const isAdmin = useIsAdmin();
  const [tab, setTab] = useState("offen");
  const [data, setData] = useState(null);
  const [open, setOpen] = useState("");
  const [reply, setReply] = useState(null);
  const [msg, setMsg] = useState("");
  const load = useCallback(async () => {
    try { setData((await axios.get(`${API(slug)}/inquiries`, { params: { status: tab } })).data); setMsg(""); window.dispatchEvent(new Event(`site-${slug}-changed`)); }
    catch (e) { setMsg(err(e, "Anfragen nicht ladbar")); }
  }, [slug, tab]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setOpen(""); }, [tab]);
  const status = async (r, s) => {
    try { await axios.post(`${API(slug)}/inquiries/${r.id}/status`, { status: s }); load(); }
    catch (e) { setMsg(err(e, "Status nicht änderbar")); }
  };
  const remove = async (r) => {
    if (!window.confirm(`Anfrage von ${r.name} endgültig löschen (z. B. Spam oder Test)?`)) return;
    try { await axios.delete(`${API(slug)}/inquiries/${r.id}`); setOpen(""); load(); }
    catch (e) { setMsg(err(e, "Löschen nicht möglich")); }
  };
  return (
    <Card testid={`site-${slug}-inquiries`}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h3 className="font-heading font-semibold">Anfragen von {title}</h3>
        <div className="flex gap-2 items-center">
          {["offen", "beantwortet", "alle"].map((k) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`site-${slug}-tab-${k}`}
              className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${tab === k ? "border-eb-primary text-eb-primary bg-white/[0.04]" : "border-white/10 text-eb-muted hover:text-eb-fg"}`}>
              {k[0].toUpperCase() + k.slice(1)} <span data-testid={`site-${slug}-count-${k}`}>{data?.counts?.[k] ?? "–"}</span></button>
          ))}
          <button className={btn} onClick={load} data-testid={`site-${slug}-inq-reload`}><RefreshCw size={13} /></button>
        </div>
      </div>
      {msg && <p className="text-sm text-rose-400 mt-3">{msg}</p>}
      {!data ? <Loader2 className="animate-spin mt-4" size={18} /> : data.items.length === 0 ? (
        <p className="text-sm text-eb-muted mt-4" data-testid={`site-${slug}-inq-empty`}>{tab === "offen" ? "Keine offenen Anfragen." : "Noch keine Anfragen."}</p>
      ) : (
        <div className="mt-3" data-testid={`site-${slug}-inq-list`}>
          {data.items.map((r) => <InquiryRow key={r.id} slug={slug} r={r} open={open === r.id} toggle={() => setOpen(open === r.id ? "" : r.id)} onReply={setReply} onStatus={status} onDelete={remove} isAdmin={isAdmin} />)}
        </div>
      )}
      <WeeklyReport slug={slug} isAdmin={isAdmin} />
      <ReplyDialog slug={slug} inq={reply} onClose={() => setReply(null)} onDone={() => { setReply(null); load(); }} />
    </Card>
  );
}

function Bars({ days }) {
  const max = Math.max(1, ...days.map((d) => d.visitors));
  const day = (iso) => { const d = new Date(`${iso}T12:00:00`); return `${d.getDate()}.${d.getMonth() + 1}.`; };
  return (
    <div>
      <div className="flex items-end gap-[3px] h-28">
        {days.map((d) => (
          <div key={d.date} className="flex-1 h-full flex flex-col justify-end group" title={`${d.date}: ${d.visitors} Besucher · ${d.pageviews} Aufrufe`}>
            <div className="rounded-t bg-sky-400/70 group-hover:bg-sky-300 transition-colors" style={{ height: `${(100 * d.visitors) / max}%`, minHeight: d.visitors ? 3 : 1 }} />
          </div>
        ))}
      </div>
      <div className="flex justify-between text-[10px] text-eb-muted mt-1">
        <span>{day(days[0].date)}</span><span>{day(days[Math.floor(days.length / 2)].date)}</span><span>heute</span>
      </div>
    </div>
  );
}

function TopList({ title, items, testid, empty }) {
  const max = Math.max(1, ...items.map((i) => i.n));
  return (
    <div data-testid={testid}>
      <div className="text-[11px] text-eb-muted uppercase tracking-wider mb-2">{title}</div>
      {items.length ? items.map((i) => (
        <div key={i.key} className="relative text-xs py-1 px-2 rounded mb-1 overflow-hidden">
          <div className="absolute inset-y-0 left-0 bg-white/5" style={{ width: `${(100 * i.n) / max}%` }} />
          <div className="relative flex justify-between gap-2"><span className="truncate">{i.title}</span><b>{i.n}</b></div>
        </div>
      )) : <p className="text-xs text-eb-muted">{empty}</p>}
    </div>
  );
}

function VisitorStats({ slug, host }) {
  const [d, setD] = useState(null);
  const [msg, setMsg] = useState("");
  useEffect(() => { axios.get(`${API(slug)}/stats`).then((r) => setD(r.data)).catch((e) => setMsg(err(e, "Besucherzahlen nicht ladbar"))); }, [slug]);
  if (!d) return msg ? <p className="text-sm text-rose-400" data-testid={`site-${slug}-stats-error`}>{msg}</p> : null;
  const t = d.totals;
  return (
    <div className="bg-eb-surface border border-white/5 rounded-xl p-5 space-y-4" data-testid={`site-${slug}-stats`}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="font-heading font-semibold flex items-center gap-2"><BarChart3 size={18} className="text-sky-300" /> Besucher auf {host}</h2>
        <span className="text-[11px] text-eb-muted">ohne Cookies · letzte 30 Tage</span>
      </div>
      {!t.pageviews && <p className="text-xs text-amber-300" data-testid={`site-${slug}-stats-empty`}>Noch keine Besuche gezählt – Zählung startet, sobald das Einbau-Snippet unten auf der Seite aktiv ist.</p>}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat label="Besucher heute" value={d.today.visitors} testid={`site-${slug}-stats-today`} />
        <Stat label="Besucher gestern" value={d.yesterday ? d.yesterday.visitors : "–"} testid={`site-${slug}-stats-yesterday`} />
        <Stat label="Besucher 30 Tage" value={t.visitors} testid={`site-${slug}-stats-visitors`} />
        <Stat label="Seitenaufrufe 30 Tage" value={t.pageviews} testid={`site-${slug}-stats-pageviews`} />
      </div>
      <Bars days={d.days} />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <TopList title="Beliebteste Seiten" items={d.top_pages} testid={`site-${slug}-stats-pages`} empty="Noch keine Aufrufe" />
        <TopList title="Woher die Besucher kommen" items={d.sources.map((s) => ({ ...s, title: s.key }))} testid={`site-${slug}-stats-sources`} empty="Noch keine Besucher" />
      </div>
    </div>
  );
}

export function SitePanel({ slug }) {
  const [meta, setMeta] = useState(null);
  const [msg, setMsg] = useState("");
  useEffect(() => { axios.get(`${API(slug)}/meta`).then((r) => setMeta(r.data)).catch((e) => setMsg(err(e, "Status nicht ladbar"))); }, [slug]);
  if (!meta) return msg ? <p className="text-sm text-rose-400">{msg}</p> : <Loader2 className="animate-spin" size={18} />;
  return (
    <div className="space-y-5" data-testid={`site-${slug}-panel`}>
      <div>
        <h1 className="font-heading text-2xl font-bold flex items-center gap-2"><Globe size={22} /> {meta.name}</h1>
        <p className="text-sm text-eb-muted mt-1 flex items-center gap-2">
          <a href={meta.url} target="_blank" rel="noreferrer" className="underline decoration-white/20 underline-offset-2 hover:decoration-current" data-testid={`site-${slug}-open-site`}>{meta.host}</a>
          <ExternalLink size={12} /> · {meta.has_inquiries ? "Anfragen beantworten, Besucher sehen, Wochenbericht versenden." : "Live-Status und Besucher im Blick behalten."}
        </p>
      </div>
      {meta.has_inquiries && <Inquiries slug={slug} title={meta.name} />}
      {meta.has_easybooking && <EasybookingCard slug={slug} />}
      <VisitorStats slug={slug} host={meta.host} />
      <LiveStatusCard slug={slug} />
      <EmbedCard slug={slug} meta={meta} />
      <Card testid={`site-${slug}-google-card`}><SiteGoogle slug={slug} host={meta.host} standalone /></Card>
    </div>
  );
}
